/**
 * Crescent — Capabilities index + Capability detail (single file, two components).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Loader2, AlertTriangle, CheckCircle2, Printer, Layers, Wind, Scissors, Package, ShieldCheck } from "lucide-react";
import CrescentLayout from "./CrescentLayout";
import usePageSeo, { useBreadcrumbSchema } from "@/hooks/usePageSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON = {
  printer: Printer, layers: Layers, wind: Wind, scissors: Scissors,
  package: Package, "shield-check": ShieldCheck,
};

// ---------------------------------------------------------------------------
// Capabilities Index — /crescent/capabilities
// ---------------------------------------------------------------------------
export function CrescentCapabilitiesIndex() {
  const [items, setItems] = useState(null);

  usePageSeo({
    title: "Our Capabilities — Crescent Flexi Pack",
    description: "Single-site, full-stack flexible packaging manufacturing — film extrusion, rotogravure printing, lamination, slitting, pouch making and in-process quality control.",
  });
  useBreadcrumbSchema([
    { name: "Home", url: "/crescent" },
    { name: "Capabilities", url: "/crescent/capabilities" },
  ]);

  useEffect(() => {
    axios.get(`${API}/crescent/capabilities`).then((r) => setItems(r.data.items)).catch(() => setItems([]));
  }, []);

  return (
    <CrescentLayout>
      <div data-testid="crescent-capabilities-index">
        <section className="bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 text-white py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="text-xs text-amber-100 mb-3">
              <Link to="/crescent" className="hover:underline">Home</Link>
              <span className="mx-2">›</span>
              <span className="text-white">Capabilities</span>
            </nav>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl leading-tight">Our Capabilities</h1>
            <p className="mt-3 text-white/90 max-w-3xl text-lg">
              A single-site, full-stack manufacturer — film extrusion, rotogravure printing, lamination,
              slitting and pouch making all under one roof.
            </p>
          </div>
        </section>

        <section className="py-14">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            {items === null && <div className="text-center py-10 text-slate-400"><Loader2 className="mx-auto animate-spin" size={24} /></div>}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="capabilities-list">
              {(items || []).map((c) => {
                const Icon = ICON[c.icon] || Package;
                return (
                  <Link
                    key={c.slug}
                    to={`/crescent/capabilities/${c.slug}`}
                    data-testid={`capability-card-${c.slug}`}
                    className="group bg-white border border-slate-200 rounded-2xl p-6 hover:border-emerald-500 hover:shadow-xl transition-all"
                  >
                    <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
                      <Icon size={22} />
                    </div>
                    <h3 className="font-heading font-bold text-slate-900 text-lg mt-4 group-hover:text-emerald-700">{c.title}</h3>
                    <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{c.short}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-700">
                      Deep-dive <ArrowRight size={14} />
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      </div>
    </CrescentLayout>
  );
}

// ---------------------------------------------------------------------------
// Capability Detail — /crescent/capabilities/:slug
// ---------------------------------------------------------------------------
export default function CrescentCapabilityDetail() {
  const { slug } = useParams();
  const [cap, setCap] = useState(null);
  const [state, setState] = useState("loading");

  useEffect(() => {
    axios.get(`${API}/crescent/capabilities/${slug}`)
      .then((r) => { setCap(r.data.capability); setState("ok"); })
      .catch(() => setState("not-found"));
  }, [slug]);

  usePageSeo(cap
    ? { title: `${cap.title} — Crescent Flexi Pack`, description: (cap.overview || cap.short || "").slice(0, 170) }
    : { title: "Loading…", noindex: true }
  );
  useBreadcrumbSchema(cap ? [
    { name: "Home", url: "/crescent" },
    { name: "Capabilities", url: "/crescent/capabilities" },
    { name: cap.title, url: `/crescent/capabilities/${slug}` },
  ] : []);

  if (state === "loading") {
    return <CrescentLayout><div className="py-24 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" size={24} /></div></CrescentLayout>;
  }
  if (state === "not-found" || !cap) {
    return (
      <CrescentLayout>
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <AlertTriangle size={32} className="mx-auto text-amber-500" />
          <h1 className="mt-3 font-heading text-3xl font-bold text-slate-900">Capability not found</h1>
          <Link to="/crescent/capabilities" className="mt-5 inline-flex items-center gap-2 text-emerald-700 font-semibold">
            View all capabilities <ArrowRight size={14} />
          </Link>
        </div>
      </CrescentLayout>
    );
  }

  const Icon = ICON[cap.icon] || Package;

  return (
    <CrescentLayout>
      <div data-testid={`crescent-capability-${slug}`}>
        <section className="bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 text-white py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div>
              <nav className="text-xs text-amber-100 mb-3">
                <Link to="/crescent" className="hover:underline">Home</Link>
                <span className="mx-2">›</span>
                <Link to="/crescent/capabilities" className="hover:underline">Capabilities</Link>
                <span className="mx-2">›</span>
                <span className="text-white">{cap.title}</span>
              </nav>
              <div className="w-14 h-14 rounded-2xl bg-white/10 border border-white/20 flex items-center justify-center">
                <Icon size={26} className="text-amber-200" />
              </div>
              <h1 className="font-heading font-extrabold text-4xl sm:text-5xl leading-tight mt-5" data-testid="capability-title">{cap.title}</h1>
              <p className="mt-4 text-lg text-white/90 max-w-xl">{cap.short}</p>
            </div>
            <div className={`w-full rounded-2xl shadow-2xl aspect-[4/3] bg-cover bg-center bg-gradient-to-br ${cap.fallback_gradient || 'from-emerald-700 to-slate-900'}`}
              style={cap.image ? { backgroundImage: `url(${cap.image})` } : {}} />
          </div>
        </section>

        {cap.overview && (
          <section className="py-14">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <p className="text-lg text-slate-700 leading-relaxed">{cap.overview}</p>
            </div>
          </section>
        )}

        {cap.specs?.length > 0 && (
          <section className="pb-14">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="font-heading font-extrabold text-slate-900 text-3xl mb-6">Specifications</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="capability-specs">
                {cap.specs.map((s, i) => (
                  <div key={i} className="bg-white border border-slate-200 rounded-xl p-5">
                    <h3 className="font-bold text-slate-900 text-base">{s.heading}</h3>
                    <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{s.detail}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {cap.process?.length > 0 && (
          <section className="pb-14 bg-slate-50">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
              <h2 className="font-heading font-extrabold text-slate-900 text-3xl mb-6">Our process</h2>
              <ol className="space-y-3" data-testid="capability-process">
                {cap.process.map((step, i) => (
                  <li key={i} className="flex items-start gap-3 bg-white border border-slate-200 rounded-xl p-4">
                    <div className="w-9 h-9 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold shrink-0">{i + 1}</div>
                    <div className="flex items-start gap-2 text-slate-700 text-sm pt-2">
                      <CheckCircle2 size={14} className="text-emerald-500 mt-0.5 shrink-0" />
                      {step}
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </section>
        )}

        <section className="py-14 bg-gradient-to-br from-emerald-600 to-emerald-800 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading font-extrabold text-3xl">Ready to specify your packaging?</h2>
            <p className="mt-3 text-emerald-100">Our team will confirm the right structure, barrier and format for your application in writing.</p>
            <Link to="/crescent/request-quote" className="mt-6 inline-flex items-center gap-2 bg-white text-emerald-800 font-bold px-6 py-3 rounded-full">
              Request a Quote <ArrowRight size={16} />
            </Link>
          </div>
        </section>
      </div>
    </CrescentLayout>
  );
}
